/**
 * Deterministic value→color assignment + numeric break computation. The same
 * category value maps to the same color across reloads and embeds because the
 * assignment is a pure function of the column's value SET (not the filtered
 * view): distinct values are ordered by frequency (desc) then value (asc) and
 * assigned palette slots in sequence — never cycled — with everything past the
 * palette cap folded into the "Other" bucket. Per-value overrides always win.
 */

const NON_EMPTY = (v: string): boolean => v.trim() !== "";

export interface CategoryEntry {
  value: string;
  color: string;
  isOther?: boolean;
}

export interface CategoryColoring {
  /** Values that received their own palette slot, in assignment order. */
  order: string[];
  /** value → hex for every value that renders as itself (incl. overridden). */
  colorFor: Record<string, string>;
  /** Values folded into the Other bucket. */
  otherValues: string[];
  other: string;
  hasOther: boolean;
  /** Legend rows (ordered slots, then a single "Other" row if needed). */
  entries: CategoryEntry[];
}

/**
 * Assign categorical colors. `values` is every point's value for the column
 * (duplicates expected — frequency drives ordering). `palette` is the fixed
 * qualitative set; the cap is its length (never cycled).
 */
export function categoryColoring(
  values: string[],
  palette: string[],
  other: string,
  overrides: Record<string, string> = {},
): CategoryColoring {
  const freq = new Map<string, number>();
  for (const v of values) {
    if (!NON_EMPTY(v)) continue;
    freq.set(v, (freq.get(v) ?? 0) + 1);
  }
  const distinct = [...freq.keys()].sort((a, b) => {
    const d = freq.get(b)! - freq.get(a)!;
    return d !== 0 ? d : a < b ? -1 : a > b ? 1 : 0;
  });

  const cap = palette.length;
  const order = distinct.slice(0, cap);
  const otherValues = distinct.slice(cap);

  const colorFor: Record<string, string> = {};
  order.forEach((v, i) => {
    colorFor[v] = overrides[v] ?? palette[i]!;
  });
  // An override targeting an Other-bucket value pulls it back out as itself.
  for (const v of otherValues) {
    if (overrides[v]) colorFor[v] = overrides[v]!;
  }

  const entries: CategoryEntry[] = order.map((v) => ({ value: v, color: colorFor[v]! }));
  const hasOther = otherValues.some((v) => !overrides[v]);
  if (hasOther) entries.push({ value: "Other", color: other, isOther: true });

  return { order, colorFor, otherValues, other, hasOther, entries };
}

export type BreakMethod = "quantile" | "equal";

/**
 * Compute `steps` ascending break points spanning the numeric values. Quantile
 * spreads breaks by rank (robust to outliers); equal splits the min→max range.
 * Returns [min, ...interior, max]; a degenerate range collapses to two.
 */
export function numericBreaks(values: number[], steps: number, method: BreakMethod = "quantile"): number[] {
  const nums = values.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (nums.length === 0) return [0, 1];
  const min = nums[0]!;
  const max = nums[nums.length - 1]!;
  if (min === max) return [min, min + 1];
  const n = Math.max(2, steps);
  const breaks: number[] = [];
  if (method === "equal") {
    for (let i = 0; i < n; i++) breaks.push(min + ((max - min) * i) / (n - 1));
  } else {
    for (let i = 0; i < n; i++) {
      const q = i / (n - 1);
      const pos = q * (nums.length - 1);
      const lo = Math.floor(pos);
      const hi = Math.ceil(pos);
      breaks.push(nums[lo]! + (nums[hi]! - nums[lo]!) * (pos - lo));
    }
  }
  // Ensure strictly ascending (quantile ties can repeat) so interpolate is valid.
  for (let i = 1; i < breaks.length; i++) {
    if (breaks[i]! <= breaks[i - 1]!) breaks[i] = breaks[i - 1]! + 1e-6;
  }
  return breaks;
}
